import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type { CreateDashboardInput } from "@lib/types/dashboards";
import { captureDashboardCreated } from "@/lib/insights/custom-insights-analytics";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import {
  createDashboardOnServer,
  invalidateDashboardsListQuery,
} from "./utils";

type UseCreateDashboardOptions = {
  invalidateListOnSuccess?: boolean;
};

export function useCreateDashboard(options: UseCreateDashboardOptions = {}) {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { selectedOrganisation } = useSelectedStores();
  const { invalidateListOnSuccess = true } = options;

  return useMutation({
    mutationFn: async (input: CreateDashboardInput) => {
      if (!selectedOrganisation) {
        throw new Error("Organisation ID is required");
      }

      return createDashboardOnServer(selectedOrganisation, input);
    },
    onSuccess: async (newDashboard) => {
      captureDashboardCreated(posthog, {
        dashboardId: newDashboard.id,
        source: "manual",
      });

      if (invalidateListOnSuccess) {
        await invalidateDashboardsListQuery(queryClient, selectedOrganisation);
      }
    },
    onError: (error) => {
      console.error("Failed to create dashboard:", error);
    },
  });
}
